import React, { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout, { ALL_MENU_ITEMS } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShieldCheck, Save, RotateCcw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

// Roles that a company admin can configure. SUPERADMIN is not editable here.
// Order = display order in the dropdown.
const EDITABLE_ROLES = [
  'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER',
  'ENGINEER', 'CS_SUPERVISOR', 'TECHNICIAN',
  'ACCOUNTANT', 'PROCUREMENT_OFFICER', 'STOREKEEPER',
  'SECURITY_OFFICER', 'HR_MANAGER', 'TENANT',
];

// Human-friendly labels for the dropdown.
const ROLE_LABEL = {
  FULL_ACCESS: 'Full Access (owner)',
  ADMIN: 'Admin',
  CEO: 'CEO',
  MANAGER: 'Manager',
  ENGINEER: 'Engineer / Supervisor',
  CS_SUPERVISOR: 'CS Supervisor',
  TECHNICIAN: 'Technician',
  ACCOUNTANT: 'Accountant',
  PROCUREMENT_OFFICER: 'Purchaser (Procurement Officer)',
  STOREKEEPER: 'Storekeeper',
  SECURITY_OFFICER: 'Security Officer',
  HR_MANAGER: 'HR Manager',
  TENANT: 'Tenant',
};

function defaultPathsForRole(role) {
  return ALL_MENU_ITEMS
    .filter(m => m.roles && (m.roles.includes('*') || m.roles.includes(role)))
    .map(m => m.path);
}

export default function RoleAccess() {
  const { user } = useContext(AuthContext);
  const [role, setRole] = useState('MANAGER');
  const [allPerms, setAllPerms] = useState([]);     // all role rows for the company
  const [allowed, setAllowed] = useState(new Set()); // currently edited set
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const canManage = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'].includes(user?.role);

  const fetchPerms = async () => {
    try {
      const r = await axios.get(`${API}/role-permissions`, { withCredentials: true });
      setAllPerms(r.data || []);
    } catch { toast.error('Failed to load role permissions'); }
  };

  useEffect(() => { fetchPerms(); }, []);

  // When role changes, hydrate `allowed` from stored perms (or defaults)
  useEffect(() => {
    const stored = allPerms.find(p => p.role === role);
    if (stored && stored.allowed_menu_paths) {
      setAllowed(new Set(stored.allowed_menu_paths));
    } else {
      setAllowed(new Set(defaultPathsForRole(role)));
    }
    setLoaded(true);
  }, [role, allPerms]);

  const toggle = (path) => {
    setAllowed(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/role-permissions/${role}`, {
        allowed_menu_paths: Array.from(allowed)
      }, { withCredentials: true });
      toast.success(`${role} permissions saved`);
      fetchPerms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const resetToDefaults = async () => {
    if (!window.confirm(`Reset ${role} to system defaults? This removes your custom menu whitelist for this role.`)) return;
    try {
      await axios.delete(`${API}/role-permissions/${role}`, { withCredentials: true });
      toast.success(`${role} reset to defaults`);
      fetchPerms();
    } catch {
      toast.error('Reset failed');
    }
  };

  const storedForRole = useMemo(() => allPerms.find(p => p.role === role), [allPerms, role]);

  return (
    <Layout>
      <div data-testid="role-access-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-amber-600" />Role Access Control
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure which menu modules each role can access in <strong>your company</strong>. Changes apply only to your tenant — other companies are unaffected.
          </p>
        </div>

        {!canManage && (
          <Card className="p-4 bg-rose-50 border-rose-200 text-sm text-rose-800">
            You do not have permission to edit role access. Contact your company admin.
          </Card>
        )}

        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-medium text-slate-500">Role to configure</label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EDITABLE_ROLES.map(r => (
                    <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{ROLE_LABEL[r] || r.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground mt-4">
              {storedForRole ? (
                <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" />Custom config saved</span>
              ) : (
                <span>Using system defaults</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button data-testid="reset-role-btn" variant="outline" onClick={resetToDefaults} disabled={!canManage || !storedForRole}>
                <RotateCcw className="w-4 h-4 mr-1" />Reset to defaults
              </Button>
              <Button data-testid="save-role-btn" onClick={save} disabled={!canManage || saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save Permissions'}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-foreground">Allowed modules for {ROLE_LABEL[role] || role}</h2>
            <div className="text-xs text-muted-foreground">{allowed.size} of {ALL_MENU_ITEMS.length} selected</div>
          </div>
          {/* Group modules by their `group` from Layout so the long list stays scannable */}
          {(() => {
            const GROUP_LABEL = {
              overview: 'Overview', operations: 'Operations', security: 'Security',
              people: 'People & HR', finance: 'Finance & Accounting',
              procurement: 'Procurement & Stock', compliance: 'Compliance & Documents',
              insights: 'Insights & Reports', admin: 'Administration',
            };
            const grouped = ALL_MENU_ITEMS.reduce((acc, m) => {
              const g = m.group || 'other';
              (acc[g] = acc[g] || []).push(m);
              return acc;
            }, {});
            const groupOrder = ['overview', 'operations', 'security', 'people', 'procurement', 'finance', 'compliance', 'insights', 'admin', 'other'];
            return groupOrder.filter(g => grouped[g]).map(g => (
              <div key={g} className="mb-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{GROUP_LABEL[g] || g}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {grouped[g].map(m => {
                    const on = allowed.has(m.path);
                    return (
                      <button
                        key={m.path}
                        type="button"
                        data-testid={`perm-toggle-${m.path.replace(/\//g, '')}`}
                        onClick={() => canManage && loaded && toggle(m.path)}
                        disabled={!canManage}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm text-left transition ${
                          on ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        } ${!canManage ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span className="truncate"><span className="font-medium">{m.label}</span> <span className="text-xs text-muted-foreground">· {m.path}</span></span>
                        <span className={`ml-2 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300'}`}>
                          {on && <CheckCircle2 className="w-3 h-3" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </Card>

        <Card className="p-4 bg-slate-50 text-xs text-slate-600">
          <div className="font-semibold text-slate-800 mb-1">How this works</div>
          When a user logs in, the frontend loads their role&apos;s whitelist. Only modules in the whitelist show in the menu.
          System-level checks on the backend still apply (an auditor/accountant cannot create buildings even if their menu is checked).
          If no custom whitelist exists for a role, system defaults are used.
        </Card>
      </div>
    </Layout>
  );
}
